import authService from './authService';

class BountyService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'https://api.scai.sh';
  }

  async makeRequest(endpoint, options = {}) {
    const token = await authService.getBackendToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseURL}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // 创建悬赏
  async createBounty(bountyData) {
    return this.makeRequest('/api/bounty/create', {
      method: 'POST',
      body: JSON.stringify(bountyData),
    });
  }

  // 获取悬赏列表
  async getBounties(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.status) queryParams.append('status', params.status);
    if (params.sort) queryParams.append('sort', params.sort);

    const queryString = queryParams.toString();
    const endpoint = `/api/bounty/list${queryString ? `?${queryString}` : ''}`;
    
    return this.makeRequest(endpoint);
  }

  // 获取统计信息
  async getStatistics() {
    const response = await this.makeRequest('/api/bounty/statistics');
    return response.statistics;
  }

  // 获取用户相关悬赏
  async getUserBounties(walletAddress, type = 'created') {
    return this.makeRequest(`/api/bounty/user/${walletAddress}?type=${type}`);
  }

  // 获取单个悬赏详情
  async getBountyById(bountyId) {
    return this.makeRequest(`/api/bounty/${bountyId}`);
  }

  // 检查DOI是否有悬赏
  async checkBountyByDOI(doi) {
    return this.makeRequest(`/api/bounty/check/${encodeURIComponent(doi)}`);
  }

  // 获取悬赏钱包地址
  async getBountyWallet() {
    const response = await this.makeRequest('/api/bounty/wallet');
    return response.wallet_address;
  }

  // 验证Solana交易
  async verifyTransaction(txId) {
    return this.makeRequest('/api/bounty/verify-tx', {
      method: 'POST',
      body: JSON.stringify({ tx_id: txId }),
    });
  }

  // 格式化金额显示
  formatAmount(amount) {
    return new Intl.NumberFormat().format(amount);
  }

  // 格式化日期显示
  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  // 计算悬赏状态
  getBountyStatus(bounty) {
    if (bounty.is_completed) {
      return 'completed';
    }
    
    if (bounty.expires_at && new Date(bounty.expires_at) < new Date()) {
      return 'expired';
    }
    
    return 'active';
  }

  // 获取状态颜色
  getStatusColor(status) {
    switch (status) {
      case 'active':
        return 'green';
      case 'completed':
        return 'blue';
      case 'expired':
        return 'red';
      default:
        return 'default';
    }
  }

  // 验证DOI格式
  validateDOI(doi) {
    const doiRegex = /^10\.\d{4,}\/[^\s]+$/;
    return doiRegex.test(doi);
  }

  // 验证悬赏金额
  validateAmount(amount) {
    return amount >= 1000 && amount <= 10000;
  }

  // 生成悬赏摘要
  generateBountySummary(bounties) {
    const active = bounties.filter(b => this.getBountyStatus(b) === 'active');
    const completed = bounties.filter(b => this.getBountyStatus(b) === 'completed');
    
    return {
      total: bounties.length,
      active: active.length,
      completed: completed.length,
      totalAmount: bounties.reduce((sum, b) => sum + b.scai_amount, 0),
      activeAmount: active.reduce((sum, b) => sum + b.scai_amount, 0),
      completedAmount: completed.reduce((sum, b) => sum + b.scai_amount, 0),
    };
  }
}

const bountyService = new BountyService();
export default bountyService;
